/**
 * Canonical business-account role taxonomy — the SINGLE source for these values.
 * `@stopal/db` builds the `business_account_role` Postgres enum FROM this array
 * (`pgEnum('business_account_role', BUSINESS_ACCOUNT_ROLES)`), so the two can't
 * drift and no lockstep test is needed. This leaf package is the shared home
 * because the frontend can import it but can't import `@stopal/db`.
 *
 * **This is a different axis from `UserRole`, and the distinction is
 * load-bearing.** A `UserRole` gates the platform SURFACE a person reaches
 * (`business` lets them into the Stopal Business app at all). A
 * `BusinessAccountRole` says only which set of permissions a member holds
 * INSIDE their own account, and grants nothing platform-wide. That is also why
 * these values do not live in `user_privilege`: that table is single-tenant
 * with no scope column, so a grant there would apply everywhere rather than
 * within one company.
 *
 * **A role is a NAME for a permission set, not a capability.** Since accounts
 * may customize what each of these may do, nothing checks a role value —
 * `@stopal/shared/business-permission` holds what is actually enforced, and
 * these four are the presets it starts from.
 */
export const BUSINESS_ACCOUNT_ROLES = [
  // Full access, including billing and the team roster.
  'admin',
  // Books pickups and manages the account's pallets.
  'manager',
  // Read-only.
  'viewer',
  // Keeps inventory: scans pallets in, photographs them, fills inventory.
  'scanner',
] as const

export type BusinessAccountRole = (typeof BUSINESS_ACCOUNT_ROLES)[number]

/**
 * Per-role display attributes — one registry, read by every surface.
 *
 * The Ops account page and the business Team page both render these strings;
 * keeping a second copy in either app is how the two drift apart. `order` is
 * the display order, so no two surfaces can sort the roster differently.
 *
 * `description` describes the PRESET — what the role does in an account that
 * has not customized it. A surface showing what a role may do in ONE account
 * reads the permissions editor's own output instead, since the two disagree the
 * moment an Admin changes anything.
 */
export const BUSINESS_ACCOUNT_ROLE_META: Record<
  BusinessAccountRole,
  { label: string; description: string; order: number }
> = {
  admin: {
    label: 'Admin',
    description: 'Full access, including billing and team',
    order: 1,
  },
  manager: {
    label: 'Manager',
    description: 'Book pickups and manage pallets',
    order: 2,
  },
  viewer: {
    label: 'Viewer',
    description: 'Read-only access',
    order: 3,
  },
  scanner: {
    label: 'Scanner',
    description: 'Scan pallets in, photograph them, and fill inventory',
    order: 4,
  },
}

/**
 * The role's label with the article it actually takes — "a Manager", but
 * "an Admin".
 *
 * Lives beside the registry because BOTH surfaces that name a role in a
 * sentence got this wrong independently: the invitee's landing page and the
 * invitation email each hardcoded "a", so inviting a second Admin — a normal
 * thing to want, since the never-zero-Admins invariant makes a spare one
 * valuable — read "invited you to join Acme as a Admin".
 *
 * Deriving from the first letter rather than storing an `article` field: the
 * four labels are ordinary words, and a stored article is a second thing to
 * keep in agreement with the label it belongs to.
 */
export function roleWithArticle(role: BusinessAccountRole): string {
  const { label } = BUSINESS_ACCOUNT_ROLE_META[role]
  return `${/^[aeiou]/i.test(label) ? 'an' : 'a'} ${label}`
}
